import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getMessages, sendMessage, markMessagesAsRead, getTotalUnreadForHR } from '../services/messaging';
import { getApplications } from '../services/api';

const CandidateChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  const candidateName = session?.profile?.full_name || session?.name || 'Candidate';

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when conversation selected
  useEffect(() => {
    if (selectedConv) setTimeout(() => inputRef.current?.focus(), 100);
  }, [selectedConv]);

  // Load conversations when widget opens
  const loadConversations = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const apps = await getApplications({ candidate_id: candidateId });
      let unreadTotal = 0;

      const convos = await Promise.all(
        apps.map(async (app) => {
          const msgs = await getMessages(app.id);
          if (msgs.length === 0) return null;

          const unread = msgs.filter(m => m.sender === 'HR' && !m.read).length;
          unreadTotal += unread;
          const lastMsg = msgs[msgs.length - 1];

          return {
            id: app.id,
            company: app.company || 'Company',
            job_title: app.job_title,
            candidate_name: app.candidate_name,
            unread_count: unread,
            last_message: lastMsg?.text || '',
            last_message_sender: lastMsg?.sender || '',
            last_message_time: lastMsg ? new Date(lastMsg.timestamp) : null,
          };
        })
      );

      setConversations(convos.filter(Boolean).sort((a, b) => {
        if (a.unread_count !== b.unread_count) return b.unread_count - a.unread_count;
        return (b.last_message_time || 0) - (a.last_message_time || 0);
      }));
      setTotalUnread(unreadTotal);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    if (open) loadConversations();
  }, [open, loadConversations]);

  // Check unread count once when widget opens (no polling)
  useEffect(() => {
    if (!candidateId || !open) return;
    const checkUnread = async () => {
      try {
        const apps = await getApplications({ candidate_id: candidateId });
        let count = 0;
        for (const app of apps) {
          const msgs = await getMessages(app.id);
          count += msgs.filter(m => m.sender === 'HR' && !m.read).length;
        }
        setTotalUnread(count);
      } catch (e) { /* silent */ }
    };
    checkUnread();
  }, [candidateId, open]);

  const loadMessages = useCallback(async (appId) => {
    const data = await getMessages(appId);
    setMessages(data);
    await markMessagesAsRead(appId, 'HR');
    setTotalUnread(prev => Math.max(0, prev - data.filter(m => m.sender === 'HR' && !m.read).length));
  }, []);

  // Load messages once when conversation is selected (no polling)
  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv.id);
  }, [selectedConv, loadMessages]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConv || sending) return;
    const text = newMessage.trim();
    setNewMessage('');
    setSending(true);
    try {
      const msg = await sendMessage(selectedConv.id, 'Candidate', text, candidateName);
      if (msg) setMessages(prev => [...prev, msg]);
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    const now = new Date();
    const h = d.getHours() % 12 || 12;
    const mins = d.getMinutes().toString().padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
    const time = `${h}:${mins} ${ampm}`;
    if (d.toDateString() === now.toDateString()) return time;
    return `${d.getDate()}/${d.getMonth() + 1} ${time}`;
  };

  const groupByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const d = new Date(msg.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      let label;
      if (d.toDateString() === today.toDateString()) label = 'Today';
      else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
      else label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(msg);
    });
    return groups;
  };

  if (!candidateId) return null;

  return (
    <>
      {/* Floating chat button */}
      <button
        onClick={() => { setOpen(!open); if (!open) setSelectedConv(null); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
      >
        {open ? (
          <Icon name="close" size={24} />
        ) : (
          <Icon name="chat" size={24} />
        )}
        {!open && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {totalUnread}
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4">
          {!selectedConv ? (
            /* ========== Conversation List ========== */
            <>
              <div className="px-4 py-3 bg-emerald-600 text-white flex-shrink-0">
                <h3 className="text-base font-bold">Messages</h3>
                <p className="text-xs text-emerald-100">Chat with recruiters</p>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-6">
                    <Icon name="chat_bubble_outline" size={48} className="text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500 text-center">No messages yet</p>
                    <p className="text-xs text-gray-400 text-center mt-1">When HR sends you a message, it will appear here</p>
                  </div>
                ) : (
                  conversations.map(conv => (
                    <div
                      key={conv.id}
                      onClick={() => setSelectedConv(conv)}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors"
                    >
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {conv.company?.charAt(0).toUpperCase() || 'C'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="text-sm font-semibold text-gray-900 truncate pr-2">{conv.company} HR</p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {conv.last_message_time ? formatTime(conv.last_message_time) : ''}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500 truncate pr-2">
                            {conv.last_message_sender === 'Candidate' && <span className="text-gray-400">You: </span>}
                            {conv.last_message}
                          </p>
                          {conv.unread_count > 0 && (
                            <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 flex-shrink-0">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">{conv.job_title}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* ========== Chat View ========== */
            <>
              {/* Header */}
              <div className="px-3 py-2.5 bg-emerald-600 text-white flex items-center gap-3 flex-shrink-0">
                <button onClick={() => setSelectedConv(null)} className="p-1 hover:bg-emerald-700 rounded-full transition-colors">
                  <Icon name="arrow_back" size={20} />
                </button>
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                  {selectedConv.company?.charAt(0).toUpperCase() || 'C'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{selectedConv.company} HR</p>
                  <p className="text-[11px] text-emerald-100 truncate">{selectedConv.job_title}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-2 bg-[#efeae2]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'400\' height=\'400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d5cfc4\' fill-opacity=\'0.15\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'2\'/%3E%3Ccircle cx=\'150\' cy=\'100\' r=\'1.5\'/%3E%3Ccircle cx=\'250\' cy=\'50\' r=\'1\'/%3E%3Ccircle cx=\'350\' cy=\'150\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")' }}>
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="bg-white/80 rounded-lg px-4 py-3 shadow-sm text-center">
                      <p className="text-xs text-gray-500">No messages yet. Say hello!</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {Object.entries(groupByDate(messages)).map(([dateLabel, msgs]) => (
                      <div key={dateLabel}>
                        <div className="flex justify-center my-2">
                          <span className="bg-white/90 text-gray-600 text-[10px] font-medium px-2.5 py-0.5 rounded-md shadow-sm">
                            {dateLabel}
                          </span>
                        </div>
                        {msgs.map(msg => (
                          <div key={msg.id} className={`flex mb-1 ${msg.sender === 'Candidate' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`relative max-w-[75%] px-3 py-1.5 rounded-lg shadow-sm ${
                              msg.sender === 'Candidate'
                                ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-none'
                                : 'bg-white text-gray-900 rounded-tl-none'
                            }`}>
                              {msg.sender === 'HR' && msg.sender_name && (
                                <p className="text-[11px] font-semibold text-blue-600 mb-0.5">{msg.sender_name}</p>
                              )}
                              <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                              <div className="flex items-center justify-end gap-1 mt-0.5">
                                <span className="text-[10px] text-gray-500">{formatTime(msg.timestamp)}</span>
                                {msg.sender === 'Candidate' && (
                                  <span className="text-[10px]">
                                    {msg.read ? <span className="text-blue-500">✓✓</span> : <span className="text-gray-400">✓✓</span>}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              <form onSubmit={handleSend} className="px-2 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message"
                    className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="w-9 h-9 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
                  >
                    <Icon name="send" size={16} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CandidateChatWidget;
